import type { MiningOrder } from '../land/miningOrders'

import { MAIN_PARTICIPANT_MAX_CHARACTERS } from '../allianceCombat'

/**
 * Mirrors backend `buildKingdomTechEffects` / `getTechnologyStatus.kingdomTechEffects`.
 * `equipmentPurchaseUnlocked` and `heroPurchaseUnlocked` gate Eclipsera-point (non-ECS) hero/equipment flows when those exist; ECS shop uses ECS balance only.
 * `mvmAttackerCastleDefenseBonusPct` boosts attacker defense in MvM castle fights (offline power + hex armor).
 */
export interface KingdomTechEffects {
  mining10m: boolean
  mining20m: boolean
  mining1h4h: boolean
  mining8h: boolean
  mining24h: boolean
  repairCostTimeReductionPct: number
  upgradeCostTimeReductionPct: number
  trainingTimeReductionPct: number
  mvmHeroLimit: number
  marketplaceUnlocked: boolean
  trainingGroundUnlocked: boolean
  resourceStealingUnlocked: boolean
  equipmentPurchaseUnlocked: boolean
  equipmentSellingUnlocked: boolean
  heroPurchaseUnlocked: boolean
  heroSellingUnlocked: boolean
  royaltyDecreaseUnlocked: boolean
  withdrawUnlocked: boolean
  counterEspionageUnlocked: boolean
  enemyHeroExecutionUnlocked: boolean
  /** Best tier: 10 / 20 / 30 / 40 — reduces minimum diplomacy points when fulfilling a gift request (payer). */
  ceasefireTributeReductionPct: number
  /** Best tier: 10–40 — reduces alliance tribute diplomacy points the paying leader must send. */
  allianceTributeReductionPct: number
  allianceDirectAidUnlocked: boolean
  marriageAllianceUnlocked: boolean
  /** Kingdom slot 0 — attack supply refill during MvM battle (UI/backend gate when the action exists). */
  attackSupplyRefillUnlocked: boolean
  /** Kingdom slot 2 — request leadership duel when alliance governance is below threshold. */
  dictatorPunishmentUnlocked: boolean
  /** Imperial slot 7 — recruit captured enemy general (MvM captives). */
  capturedGeneralConversionUnlocked: boolean
  /** Best tier 0 / 10 / 30 / 50 / 70 / 100 — attacker defense in MvM castle fights (offline power + hex armor). */
  mvmAttackerCastleDefenseBonusPct: number
}

export const DEFAULT_KINGDOM_TECH_EFFECTS: KingdomTechEffects = {
  mining10m: false,
  mining20m: false,
  mining1h4h: false,
  mining8h: false,
  mining24h: false,
  repairCostTimeReductionPct: 0,
  upgradeCostTimeReductionPct: 0,
  trainingTimeReductionPct: 0,
  mvmHeroLimit: 3,
  marketplaceUnlocked: false,
  trainingGroundUnlocked: false,
  resourceStealingUnlocked: false,
  equipmentPurchaseUnlocked: false,
  equipmentSellingUnlocked: false,
  heroPurchaseUnlocked: false,
  heroSellingUnlocked: false,
  royaltyDecreaseUnlocked: false,
  withdrawUnlocked: false,
  counterEspionageUnlocked: false,
  enemyHeroExecutionUnlocked: false,
  ceasefireTributeReductionPct: 0,
  allianceTributeReductionPct: 0,
  allianceDirectAidUnlocked: false,
  marriageAllianceUnlocked: false,
  attackSupplyRefillUnlocked: false,
  dictatorPunishmentUnlocked: false,
  capturedGeneralConversionUnlocked: false,
  mvmAttackerCastleDefenseBonusPct: 0,
}

/** Matches backend `getWalletMvmParticipantHeroCap` for main attacker/defender MvM rosters. */
export function resolveMvmMainParticipantHeroCap(
  mvmHeroLimit: number | null | undefined,
  absoluteMax: number = MAIN_PARTICIPANT_MAX_CHARACTERS,
): number {
  const cap = Number(mvmHeroLimit)
  const n = Number.isFinite(cap) ? Math.floor(cap) : DEFAULT_KINGDOM_TECH_EFFECTS.mvmHeroLimit
  return Math.min(absoluteMax, Math.max(3, n))
}

export function isMiningOrderDurationAllowed(
  orders: readonly MiningOrder[],
  durationSec: number,
  effects: KingdomTechEffects,
): boolean {
  if (!orders?.length) return false
  const d = Number(durationSec)
  const base = Number(orders[0]?.durationSec)
  if (!Number.isFinite(d) || d <= 0) return false
  if (Number.isFinite(base) && d === base) return true
  if (d >= 86400) return !!effects.mining24h
  if (d >= 28800) return !!effects.mining8h
  if (d === 7200 || d >= 14400) return !!effects.mining1h4h
  if (d >= 3600) return !!effects.mining1h4h
  if (d >= 1200) return !!effects.mining20m
  if (d >= 600) return !!effects.mining10m
  return true
}

/** Highest-index mining order unlocked by kingdom tech (e.g. longest allowed duration). */
export function findLastAllowedMiningOrderIndex(
  orders: readonly MiningOrder[],
  effects: KingdomTechEffects | null | undefined,
): number {
  if (!orders.length) return -1
  if (!effects) return orders.length - 1
  for (let i = orders.length - 1; i >= 0; i -= 1) {
    const ord = orders[i]
    if (ord && isMiningOrderDurationAllowed(orders, ord.durationSec, effects)) return i
  }
  return -1
}

/** Map a duration card click to an allowed order index (locked → last unlocked). */
export function miningOrderClickIndex(
  orders: readonly MiningOrder[],
  clickedIndex: number,
  effects: KingdomTechEffects | null | undefined,
): number {
  const ord = orders[clickedIndex]
  if (!ord) return clickedIndex
  if (!effects || isMiningOrderDurationAllowed(orders, ord.durationSec, effects)) {
    return clickedIndex
  }
  const last = findLastAllowedMiningOrderIndex(orders, effects)
  return last >= 0 ? last : clickedIndex
}

/** Keep selection when allowed; otherwise move to the last unlocked order (tech research sync). */
export function syncMiningOrderIndexToKingdomTech(
  orders: readonly MiningOrder[],
  currentIndex: number,
  effects: KingdomTechEffects | null | undefined,
): number {
  if (!orders.length) return 0
  const row = orders[currentIndex]
  if (row && (!effects || isMiningOrderDurationAllowed(orders, row.durationSec, effects))) {
    return currentIndex
  }
  const last = findLastAllowedMiningOrderIndex(orders, effects)
  return last >= 0 ? last : currentIndex
}

/** Minimum diplomacy points after kingdom research % reduction (payer-side). */
export function kingdomResearchReducedPointRequirement(basePoints: number, reductionPct: number): number {
  const p = Math.min(90, Math.max(0, Number(reductionPct) || 0))
  const b = Math.max(0, Number(basePoints) || 0)
  if (b <= 0) return 0
  return Math.max(1, Math.ceil((b * (100 - p)) / 100))
}
